import numeral from 'numeral';
import proj4 from 'proj4';
import { colorsMap } from '/src/features/widgets/edit/components/chart-color.js';

export const geoJsonMap = {
  Africa: 'custom/africa',
  Europe: 'custom/europe',
  USA: 'countries/us/us-all',
  World: 'custom/world'
};

// Default options for world map widget.
export const defaultOptions = (widget) => ({
  chart: {
    map: geoJsonMap[widget.Settings.mapContentType],
    proj4
  },
  title: {
    text: null
  },
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: false
  },
  series: [],
  mapNavigation: {
    enabled: true,
    buttonOptions: {
      verticalAlign: 'top',
      style: {
        fontSize: '14px',
        fontFamily: "'Open Sans', sans-serif"
      }
    },
    enableMouseWheelZoom: false
  },
  plotOptions: {
    map: {
      dataLabels: {
        verticalAlign: 'bottom',
        format: '{point.formattedValue}',
        style: {
          fontSize: '12px',
          fontFamily: "'Open Sans', sans-serif"
        }
      }
    },
    mappoint: {
      dataLabels: {
        verticalAlign: 'top',
        format: '{point.label}',
        style: {
          fontSize: '12px',
          fontFamily: "'Open Sans', sans-serif"
        }
      }
    }
  },
  tooltip: {
    formatter() {
      return this.series.tooltipOptions.customTooltip.call(this);
    }
  }
});

// Get series to display countries.
export const countrySeries = ({ data, kpis }, groupBy, { mainKpiRef, series, worldMapSeriesColor }) => {
  const mainSeries = series.find((widgetSeries) => widgetSeries.kpiRef === mainKpiRef);
  const extraSeries = series.filter((widgetSeries) => widgetSeries.kpiRef !== mainKpiRef);
  const kpiFormats = getKpiFormats(kpis);
  const seriesData = data.map((item) => {
    // Extra series data.
    const seriesValues = getExtraSeriesMap(extraSeries, item, kpiFormats);
    return {
      ...seriesValues,
      code: (item[groupBy] || '').toLowerCase(),
      sofyCode: item[groupBy],
      value: item[mainKpiRef],
      formattedValue: kpiFormats[mainKpiRef]
        ? numeral(item[mainKpiRef]).format(kpiFormats[mainKpiRef])
        : item[mainKpiRef],
      color: getColorForPoint(
        worldMapSeriesColor.rules || [],
        item[mainKpiRef],
        worldMapSeriesColor.defaultColorV2 || worldMapSeriesColor.defaultColor
      )
    };
  });

  return {
    name: mainSeries.name,
    data: seriesData,
    dataLabels: {
      enabled: true
    },
    tooltip: {
      customTooltip: function () {
        return [
          `${this.point.name} - ${mainSeries.name}: ${this.point.formattedValue}`,
          ...extraSeries.map(({ name }) => `${name}: ${this.point[name]}`)
        ].join(', ');
      }
    },
    color: colorsMap()[worldMapSeriesColor.defaultColorV2] || worldMapSeriesColor.defaultColor,
    joinBy: ['hc-key', 'code']
  };
};

// Get series data to display coordinates.
export const coordinateSeries = ({ data: coordinatesData, kpis }, { worldMapSeriesColor, mainKpiRef, series }) => {
  const mainSeries = series.find((widgetSeries) => widgetSeries.kpiRef === mainKpiRef);
  const extraSeries = series.filter((widgetSeries) => widgetSeries.kpiRef !== mainKpiRef);
  const kpiFormats = getKpiFormats(kpis);

  const seriesData = coordinatesData.map((item) => {
    const seriesValues = getExtraSeriesMap(extraSeries, item, kpiFormats);
    const colorConfig = getColorForPoint(
      worldMapSeriesColor.rules || [],
      item[mainKpiRef],
      {
        pointColor: colorsMap()[worldMapSeriesColor.pointColorV2] || worldMapSeriesColor.pointColor,
        labelColor: colorsMap()[worldMapSeriesColor.labelColorV2] || worldMapSeriesColor.labelColor
      },
      true
    );
    return {
      ...seriesValues,
      lat: item['lat'],
      lon: item['lon'],
      label: item['label'],
      value: item[mainKpiRef],
      color: colorsMap()[colorConfig.pointColorV2] || colorConfig.pointColor,
      dataLabels: {
        color: colorsMap()[colorConfig.labelColorV2] || colorConfig.labelColor
      },
      formattedValue: kpiFormats[mainKpiRef]
        ? numeral(item[mainKpiRef]).format(kpiFormats[mainKpiRef])
        : item[mainKpiRef]
    };
  });
  return {
    data: seriesData,
    type: 'mappoint',
    tooltip: {
      // Custom tooltip. (i.e. Label, Lat: #lat, Lon: #lon, #kpiName: #value)
      customTooltip: function () {
        let basicDetail = `Lat: ${this.point.lat}, Lon: ${this.point.lon} <br> ${mainSeries.name}: ${this.point['formattedValue']}`;
        if (this.point.label) {
          basicDetail = `<b>${this.point.label}</b></br>` + basicDetail;
        }
        extraSeries.forEach((kpi) => {
          basicDetail += `<br> ${kpi.name}: ${this.point[kpi.name]}`;
        });
        return basicDetail;
      }
    },
    color: colorsMap()[worldMapSeriesColor.pointColorV2] || worldMapSeriesColor.pointColor,
    dataLabels: {
      verticalAlign: 'top',
      format: '{point.label}',
      color: colorsMap()[worldMapSeriesColor.labelColorV2] || worldMapSeriesColor.labelColor
    }
  };
};

// Get extra series map. (i.e. extraSeries = [extraSeries1, extraSeries2] => { extraSeries1: #value1, extraSeries2: #value2 })
const getExtraSeriesMap = (extraSeries, item, kpiFormats) =>
  extraSeries
    .map((series) => ({
      name: series.name,
      value: item[series.kpiRef],
      formattedValue: kpiFormats[series.kpiRef]
        ? numeral(item[series.kpiRef]).format(kpiFormats[series.kpiRef])
        : item[series.kpiRef]
    }))
    .reduce((acc, seriesValue) => {
      acc[seriesValue.name] = seriesValue.formattedValue;
      return acc;
    }, {});

// Get kpi formats.
const getKpiFormats = (kpiList = []) =>
  kpiList.reduce((acc, { UUID, Format }) => {
    acc[UUID] = Format;
    return acc;
  }, {});

// Get color based on condition.
const getColorForPoint = (rules, value, defaultColor, coordinateConfig = false) => {
  if (typeof value === 'string') value = parseFloat(value.replace('%', ''));
  let rule =
    rules.find((rule) => {
      let result = false;
      switch (rule.condition) {
        case '>':
          result = value > rule.value;
          break;
        case '>=':
          result = value >= rule.value;
          break;

        case '<':
          result = value < rule.value;
          break;
        case '<=':
          result = value <= rule.value;
          break;
        case '=':
          result = value === rule.value;
          break;
        case 'range':
          result = value >= rule.value[0] && value <= rule.value[1];
          break;
        case '<>':
          result = value !== rule.value;
          break;
      }
      return result;
    }) || {};

  if (coordinateConfig) {
    return Object.keys(rule).length
      ? {
          labelColor: colorsMap()[rule.labelColorV2] || rule.labelColor,
          pointColor: colorsMap()[rule.pointColorV2] || rule.pointColor
        }
      : defaultColor;
  }
  const color = colorsMap()[rule.colorV2] || rule.color || defaultColor;
  return colorsMap()[color] || color;
};
